import { z } from "zod";
export const lafayetteEnvironmentSchema = z.enum(["development", "test", "staging", "production"]);
export const foundationVersionSchema = z.object({
    environment: lafayetteEnvironmentSchema,
    version: z.string().min(1).max(128),
    commit: z.string().min(1).max(128),
    buildId: z.string().min(1).max(128),
});
export function apiEnvelopeSchema(dataSchema) {
    return z.object({
        data: dataSchema,
        requestId: z.string().min(1).max(128),
        correlationId: z.string().min(1).max(128),
    });
}
export const apiErrorCodeSchema = z.enum([
    "invalid_request",
    "unauthenticated",
    "forbidden",
    "not_found",
    "payload_too_large",
    "rate_limited",
    "request_timeout",
    "internal_error",
    "service_unavailable",
    "conflict",
    "unprocessable",
    // CHT-715: the agent route. `agent_unavailable`: the runtime is off or failed to start (503 with Retry-After).
    // `agent_run_in_progress`: a run with this Idempotency-Key, or another run of this conversation, is still running (409).
    "agent_unavailable",
    "agent_run_in_progress",
    // CHT-727: the customer reached the daily cap of agent runs or tokens (429 with Retry-After until the next UTC day).
    "daily_limit_reached",
]);
export const apiErrorEnvelopeSchema = z.object({
    error: z.object({
        code: apiErrorCodeSchema,
        message: z.string().min(1).max(240),
    }),
    requestId: z.string().min(1).max(128),
    correlationId: z.string().min(1).max(128),
});
export const healthStatusSchema = z.object({
    status: z.literal("ok"),
    version: foundationVersionSchema,
});
export const readinessStatusSchema = z.object({
    status: z.literal("ready"),
    version: foundationVersionSchema,
});
export const versionStatusSchema = z.object({
    version: foundationVersionSchema,
});
export const smokeRequestSchema = z.object({
    echo: z.string().trim().min(1).max(120),
});
export const smokeResponseSchema = z.object({
    echo: z.string().min(1).max(120),
    actorKind: z.enum(["anonymous", "customer", "staff", "system"]),
});
export const placeholderRouteSchema = z.object({
    group: z.enum(["customer", "staff", "agent", "imports", "payments", "webhooks"]),
    status: z.literal("foundation_ready"),
});
export const staffSessionSchema = z.object({
    subjectId: z.string().min(1).max(128),
    roles: z.array(z.enum(["concierge", "supervisor", "catalog_editor", "administrator"])),
    mfaSatisfied: z.boolean(),
    sessionId: z.string().min(1).max(128).nullable(),
    expiresAt: z.number().int().positive().nullable(),
});
export const staffInvitationRequestSchema = z.object({
    email: z.string().email().max(320),
});
export const staffInvitationResponseSchema = z.object({
    status: z.literal("invitation_sent"),
});
export * from "./agent-evidence.js";
export * from "./agent-run-api.js";
export * from "./agent-ui-parts.js";
export * from "./agent-ui-parts-presentation.js";
// CHT-769 (Phase 13.3): the analytics ingest and feature-flag routes.
export * from "./analytics.js";
export * from "./concierge-intake.js";
export * from "./conversation-handoff.js";
export * from "./customer-cards.js";
export * from "./customer-catalog-picker.js";
export * from "./customer-catalog-places.js";
export * from "./customer-memory.js";
export * from "./customer-workspace.js";
export * from "./domain-views.js";
export * from "./itinerary-edit.js";
export * from "./itinerary-location.js";
export * from "./itinerary-validation-run.js";
export * from "./lifecycle-notifications.js";
export * from "./message-part-refs.js";
export * from "./messaging.js";
export * from "./milestone-c-routes.js";
export * from "./payments.js";
// CHT-771 (Phase 13.5): the privacy routes (export, deletion, retention matrix).
export * from "./privacy.js";
export * from "./request-fulfilment.js";
export * from "./request-projection.js";
export * from "./request-questions.js";
export * from "./staff-analytics.js";
export * from "./staff-analytics-events.js";
export * from "./staff-analytics-explore.js";
export * from "./staff-attention.js";
export * from "./staff-catalog.js";
export * from "./staff-catalog-places.js";
export * from "./staff-catalog-search.js";
export * from "./staff-conversation-context.js";
export * from "./staff-copilot.js";
export * from "./staff-customer-memory.js";
// CHT-772 (Phase 13.6): the audit view, notification failures and payment exceptions of the staff diagnostics.
export * from "./staff-diagnostics.js";
// CHT-2025: the Home trends contract (numbers per tile).
export * from "./staff-home-trends.js";
export * from "./staff-imports.js";
export * from "./staff-itinerary.js";
// CHT-780 (Phase 14.7): the beta control routes (AI pause, cohort gate, cohort list).
export * from "./staff-operations-beta.js";
// CHT-761 (Milestone E, Phase 12): the staff operations jobs contract.
export * from "./staff-operations-jobs.js";
export * from "./staff-payment-quotes.js";
// CHT-770 (Phase 13.4): the provider-health route contract.
export * from "./staff-provider-health.js";
export * from "./staff-request-create.js";
export * from "./staff-requests.js";
export * from "./staff-team.js";
// CHT-2026: the per-person load and the customers without cover, for the Home dashboard.
export * from "./staff-team-load.js";
